import { Label } from "@/components/ui/label";
import { Upload } from "lucide-react";
import { useRef, useState } from "react";

interface UploadsDragAndDropProps {
  label: string;
  accept?: string;
  maxSize?: number; // em MB
  onFileSelect: (file: File) => void;
  currentFile?: File | null;
  className?: string;
  disabled?: boolean;
}

export const UploadsDragAndDrop = ({
  label,
  accept = ".pdf,.jpg,.jpeg,.png",
  maxSize = 10,
  onFileSelect,
  currentFile,
  className = "",
  disabled = false
}: UploadsDragAndDropProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragOver(true);
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    if (disabled) return;
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const file = files[0];
      if (validateFile(file)) {
        onFileSelect(file);
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && validateFile(file)) {
      onFileSelect(file);
    }
  };

  const validateFile = (file: File): boolean => {
    // Validar tipo de arquivo
    const acceptedTypes = accept.split(',').map(type => type.trim());
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    const isValidType = acceptedTypes.some(type => 
      type === fileExtension || 
      (type.startsWith('.') && fileExtension === type) ||
      (type.includes('*') && file.type.match(type.replace('*', '.*')))
    );

    if (!isValidType) {
      alert(`Tipo de arquivo não suportado. Aceitos: ${accept}`);
      return false;
    }

    // Validar tamanho
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > maxSize) {
      alert(`Arquivo muito grande. Tamanho máximo: ${maxSize}MB`);
      return false;
    }

    return true;
  };

  const handleClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  const getFileSizeText = () => {
    if (currentFile) {
      const sizeMB = (currentFile.size / (1024 * 1024)).toFixed(2);
      return `${sizeMB}MB`;
    }
    return '';
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <Label className="text-base font-semibold">{label}</Label>
      <div 
        className={`
          border-2 border-dashed rounded-lg p-8 text-center transition-colors
          ${isDragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-accent/50 cursor-pointer'}
        `}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <Upload className={`h-8 w-8 mx-auto mb-2 ${isDragOver ? 'text-primary' : 'text-muted-foreground'}`} />
        <p className="text-sm font-medium">
          {isDragOver ? 'Solte o arquivo aqui' : 'Arraste arquivos ou clique para enviar'}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          {accept} (máx {maxSize}MB)
        </p>
      </div>
      
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled}
      />
      
      {currentFile && (
        <div className="mt-2 p-3 bg-muted rounded-lg text-sm">
          <div className="flex justify-between items-start">
            <div>
              <p className="font-medium">Arquivo carregado:</p>
              <p className="text-muted-foreground">{currentFile.name}</p>
              <p className="text-xs text-muted-foreground mt-1">
                Tamanho: {getFileSizeText()}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
